import { mappingFileURL } from "./globals";


// Canvas drawing functions
/**
 * Draw image by Url
 * @param url
 * @param returnAsBase64
 * @param timeout
 * @returns {Promise<unknown>} return img object
 */
export function getImage(url, returnAsBase64 = false, timeout = 0) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        if (timeout !== 0) {
            setTimeout(() => { image.src = ""; }, timeout);
        }

        image.onload = () => {
            if (returnAsBase64) {
                const canvas = createCanvas(image.width, image.height);

                const ctx = canvas.getContext('2d');
                ctx.drawImage(image, 0, 0);
                resolve(canvas.toDataURL());
            } else {
                resolve(image);
            }
        };

        image.onerror = (err) => {
            reject(err);
        };

        image.crossOrigin = "Anonymous";
        image.src = (location.hostname === "" && url.startsWith("//") ? `https:${url}` : url).replace(/&amp;/g, '&');

    });
}

/**
 * Create canvas element
 * @param width
 * @param height
 * @returns {HTMLCanvasElement}
 */
export function createCanvas(width, height) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    return canvas;
}

/**
 * Draw image on canvas by arguments
 * @param context
 * @param args
 */
export function drawImage(context, args) {
    try {
        CanvasRenderingContext2D.prototype.drawImage.apply(context, args);
    } catch (e) { }
}

/**
 * disable scaled image smoothing
 * @param context
 */
export function disableCanvasSmoothing(context) {
    context.webkitImageSmoothingEnabled = false;
    context.imageSmoothingEnabled = false;
}

/**
 * able scaled image smoothing
 * @param context
 */
export function enableCanvasSmoothing(context) {
    context.webkitImageSmoothingEnabled = true;
    context.mozImageSmoothingEnabled = true;
    context.imageSmoothingEnabled = true;
}

/**
 * Return img object from canvas
 * @param canvas
 * @returns {HTMLImageElement}
 */
export function canvasToImage(canvas) {
    const image = new Image();
    image.src = canvas.toDataURL();
    return image;
}

// Helper Functions
/**
 * Wait for condition to be true and then proceed
 * @param condition
 * @param timeout
 * @param error
 * @returns {Promise<unknown>}
 */
export function waitFor(condition = null, timeout = 5000, error = null) {
    return new Promise((resolve, reject) => {

        if (condition()) {
            resolve();
            return;
        }

        const interval = setInterval(() => {
            if (condition()) {
                clearInterval(interval);
                resolve();
            }
        }, 50);

        setTimeout(() => {
            clearInterval(interval);
            reject(error ? error : "");
        }, timeout);
    });
}

/**
 * Validate that object has all requested params
 * @param name
 * @param obj
 * @param params
 */
export function validateObject(name, obj, params) {
    for (const param of params) {
        if (!obj.hasOwnProperty(param)) {
            error("Validation Error", `${name} ${param} is missing`)
        }
    }
}

/**
 * Return copy of the object
 * @param obj
 * @returns {{}|*[]|Date|*}
 */
export function cloneObject(obj) {
    let copy;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' !== typeof obj) return obj;

    // Handle Date
    if (obj instanceof Date) {
        copy = new Date();
        copy.setTime(obj.getTime());
        return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
        copy = [];
        for (let i = 0, len = obj.length; i < len; i++) {
            copy[i] = cloneObject(obj[i]);
        }
        return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
        copy = {};
        for (let attr in obj) {
            if (obj.hasOwnProperty(attr)) copy[attr] = cloneObject(obj[attr]);
        }
        return copy;
    }

    throw new Error("Failed to deep clone");
}

/**
 * throw new Error
 * @param subject
 * @param msg
 */
export function error(subject = "Error", msg = "") {
    throw new Error(`${subject}: ${msg}`);
}

export function warn(subject = "Error", msg = "") {
    console.warn(`${subject}: ${msg}`);
}

export function log(msg, params) {
    params ? console.log('%cSugar::', "background-color: #ccc", msg, params) : console.log('%cSugar::', "background-color: #ccc", msg)
}


// Cache Functions
/**
 * Return a unique key for item
 * @param item
 * @param isMlsdk
 * @returns {string}
 */
export function getAssetKey(item, isMlsdk = false, numPhotos = undefined) {
    return isMlsdk
        ? `scheme-${item.productCode}-${item.skuCode}-${item.formFactor}-${item.brand || "SFLY"}`
        : `layout-${item.sizeId}-${item.occasionId}-${item.styleId}-${item.layoutName || "defaultLayout"}-${item.brand || "SFLY"}${numPhotos ? '-' + numPhotos : ''}`;
}

/**
 * Get data from cache by key
 * @param key
 * @returns {*|boolean}
 */
export function cacheGet(key) {
    return window?.MagicRendererCache && window.MagicRendererCache[key] ? window.MagicRendererCache[key] : false;
}

/**
 * Set data to cache in key, value
 * @param key
 * @param data
 * @returns {*|boolean}
 */
export function cacheSet(key, data) {
    if (window) {
        if (!window.MagicRendererCache) window.MagicRendererCache = {};
        window.MagicRendererCache[key] = data;
    }
}


// Renderer mapping functions
/**
 * Load the sugarMap file
 * @param env
 * @returns {Promise<*|boolean>}
 */
export async function loadMappingFile(env) {
    try {
        let mapping = cacheGet("mapping");
        if (!mapping) {
            mapping = await (await fetch(mappingFileURL(env))).json();
            cacheSet("mapping", mapping);
        }
        return mapping;
    } catch (e) {
        error("Failure", `Failed to get mapping file from CDN ${e}`);
    }
}

export const RENDERER_TYPE_V0 = "v0";

export const RENDERER_TYPE_V1 = "v1";

export const RENDERER_TYPE_THUMBGEN = "thumbGen";

export function numericSize(size = "small", forLibSugar = false) {
    switch (size) {
        default:
        case "small":
            return 280;
        case "medium":
            return forLibSugar ? 500 : 600;
        case "large":
        case "x-large":
            return 800;
    }
}

export function textualSize(pixels) {
    let size = "small";
    if (pixels < 100) {
        size = "x-small";
    } else if (pixels < 350) {
        size = "small";
    } else if (pixels < 550) {
        size = "medium";
    } else if (pixels < 800) {
        size = "large";
    } else {
        size = "x-large";
    }
    return size;
}

/**
 * Change url for using anonymous photos
 * @param url
 * @param name
 * @returns {string|string}
 */
export function getUrlParameter(url, name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(url);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

/**
 * Get size for proc simple fit
 * @param size
 * @returns {string}
 */
export function getSizeForProcSimpleFit(size, isUmp = false) {
    switch (size) {
        case 'x-large':
            return "fit1600x1600";
        case 'large':
            return "fit800x800";
        case 'medium':
            return isUmp ? "fit700x700" : "fit350x350";
        default:
            return "fit350x350";
    }
}

/**
 * Extract the size id from layoutName
 * @param layoutName
 * @returns {number}
 */
export function extractSizeId(layoutName) {
    return layoutName && parseInt(layoutName.split("_")[0]);
}

/**
 * Check if product is folded product
 * @param IdentificationString
 * @returns {string}
 */
export function isFoldedProduct(IdentificationString) {
    return IdentificationString.toLowerCase().includes('folded');
}

/**
 * Check product trim
 * @param type
 * @param filters
 * @param IdentificationStrings
 * @returns {string}
 */
export function checkProductTrimAndFormat(type, IdentificationStrings, filters) {
    if (filters && filters['trimOptions'] && filters['trimOptions'].length > 0) {
        return (filters['trimOptions'][0].toLowerCase() === type);
    }
    else if (IdentificationStrings && IdentificationStrings.length > 0) {
        for (let i = 0; i < IdentificationStrings.length; i++) {
            if (typeof IdentificationStrings[i] === 'string' && (IdentificationStrings[i].toLowerCase().includes(type) || (type === 'rounded' && IdentificationStrings[i].toLowerCase().includes('magnet')))) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Check if product is gatefold product
 * @param IdentificationString
 * @returns {string}
 */
export function isGatefoldProduct(IdentificationString) {
    return IdentificationString.toLowerCase().includes('gatefold');
}

/**
 * Check if product is trifold product
 * @param IdentificationString
 * @returns {string}
 */
export function isTrifoldProduct(IdentificationString) {
    return IdentificationString.toLowerCase().includes('trifold');
}